import { CheckBox, OptionsButton } from '../components/common';

/**
 * Normalizes a single beacon association record.
 * @param {Object} beaconData - Raw beacon association data.
 * @returns {Object} Normalized beacon association object.
 */
export const normalizeBeaconAssociation = (beaconData) => ({
  id: beaconData.id,
  beaconMacAddress: beaconData.beacon_mac_address,
  location: beaconData.location,
  organization_id: beaconData.organization_id,
  facilityId: beaconData.facility,
});

/**
 * Prepares a beacon row for editing.
 * @param {Object} rowData - Beacon row data.
 * @returns {Object} Transformed object for editing form.
 */
export const prepareRowForEditing = (rowData) => ({
  beaconIdentifier: rowData.beacon_mac_address,
  facility: parseInt(rowData.facility, 10),
  locationName: rowData.location,
  id: rowData.id,
  organization_id: rowData?.organization_id,
});

/**
 * Transforms raw beacon data into a structured format for table display.
 * @param {Object} rawData - Raw beacon association data.
 * @param {Array} organizations - List of organizations.
 * @param {Array} facilities - List of facilities.
 * @returns {Object} Formatted beacon data for table rows.
 */
export const formatBeaconDataForTable = (rawData, organizations, facilities) => {
  const { id, beaconMacAddress, location, organization_id, facilityId } =
    normalizeBeaconAssociation(rawData);

  const organizationName =
    organizations?.find((org) => org.id === organization_id)?.name || 'Unknown';
  const facilityName = facilities?.find((fac) => fac.id === facilityId)?.name || 'Unknown';

  return { id, beaconMacAddress, location, organizationName, facilityName };
};

/**
 * Formats raw beacon data into a structured format for table export.
 * Uses Maps for efficient lookup of organization and facility names.
 *
 * @param {Array} rawData - Array of beacon objects containing raw data.
 * @param {Array} organizations - List of organization objects with `id` and `name`.
 * @param {Array} facilities - List of facility objects with `id` and `name`.
 * @returns {Array} Array of formatted objects with mapped organization and facility names.
 */
export const formatBeaconDataForTableToExport = (
  rawData = [],
  organizations = [],
  facilities = [],
) => {
  const orgMap = new Map(organizations.map((org) => [org.id, org.name]));
  const facilityMap = new Map(facilities.map((fac) => [fac.id, fac.name]));

  return rawData.map(({ organization_id, facility, beacon_mac_address, location }) => ({
    beacon_mac_address,
    location,
    organization_id: orgMap.get(organization_id) || 'Unknown',
    facility: facilityMap.get(facility) || 'Unknown',
  }));
};

/**
 * Maps a list of beacons into table row objects.
 * @param {Array} beacons - Array of beacon objects.
 * @param {Array} organizations - List of organizations.
 * @param {Array} facilities - List of facilities.
 * @param {Array} selectedRows - Array of selected row IDs.
 * @param {Function} handleCheckboxChange - Callback for handling checkbox selection.
 * @param {Boolean} isUserAdmin - Indicates if the user is an admin.
 * @param {Function} handleRowDelete - Callback for handling row deletion.
 * @param {Function} handleRowEdit - Callback for handling row editing.
 * @returns {Array} Array of formatted table row objects.
 */
export const mapBeaconsToTableRows = (
  beacons,
  organizations,
  facilities,
  selectedRows,
  handleCheckboxChange,
  isUserAdmin,
  handleRowDelete,
  handleRowEdit,
) => {
  return beacons.map((beacon) => {
    const { id, beaconMacAddress, location, organizationName, facilityName } =
      formatBeaconDataForTable(beacon, organizations, facilities);

    return {
      type: 'normal',
      id,
      data: {
        columns: [
          {
            id: 'checkbox',
            value: (
              <CheckBox
                checked={selectedRows.includes(id)}
                onChange={() => handleCheckboxChange(id)}
              />
            ),
          },
          { id: 'beaconMacAddress', value: beaconMacAddress },
          { id: 'location', value: location },
          ...(isUserAdmin ? [{ id: 'organization_id', value: organizationName }] : []),
          { id: 'facility', value: facilityName },
          {
            id: 'actions',
            value: (
              <OptionsButton
                options={[
                  { label: 'Edit', onClick: () => handleRowEdit(id) },
                  { label: 'Delete', onClick: () => handleRowDelete(id) },
                ]}
              />
            ),
          },
        ],
      },
    };
  });
};

/**
 * Generates table column configuration.
 * @param {Boolean} selectAll - Indicates if all rows are selected.
 * @param {Function} handleToggleSelectAll - Callback to toggle select-all checkbox.
 * @param {Boolean} isUserAdmin - Indicates if the user is an admin.
 * @returns {Array} Table column definitions.
 */
export const generateTableColumns = (selectAll, handleToggleSelectAll, isUserAdmin) => {
  return [
    {
      key: 'checkbox',
      label: <CheckBox checked={selectAll} onChange={() => handleToggleSelectAll(!selectAll)} />,
    },
    { key: 'beaconMacAddress', label: 'MAC Address' },
    { key: 'location', label: 'Location' },
    ...(isUserAdmin ? [{ key: 'organization_id', label: 'Organization' }] : []),
    { key: 'facility', label: 'Facility' },
    { key: 'actions', label: 'Actions' },
  ];
};

/**
 * Defines the columns for exporting beacon data.
 * This configuration is used to map beacon data to column headers for export.
 *
 * @returns {Array} An array of objects containing column headers and their corresponding fields.
 */
export const getColumnsHeaderForExportData = [
  { header: '', field: '' },
  { header: 'Beacon Identifier', field: 'beacon_mac_address' },
  { header: 'Location Name', field: 'location' },
  { header: 'Organization', field: 'organization_id' },
  { header: 'Facility', field: 'facility' },
];

/**
 * Creates parameters for editing a beacon.
 * This function structures the input data and returns an object containing the necessary parameters
 * for updating a beacon association.
 *
 * @param {Object} params - The beacon details used for editing.
 * @param {string} params.beaconIdentifier - The unique identifier for the beacon.
 * @param {string} params.locationName - The name of the location associated with the beacon.
 * @param {string} params.facility - The facility where the beacon is located.
 * @param {string} params.id - The unique ID of the beacon.
 * @param {string} params.organization_id - The organization associated with the beacon.
 *
 * @returns {Object} An object containing the parameters needed to edit the beacon.
 */
export const createBeaconParamsToEdit = ({
  beaconIdentifier,
  locationName,
  facility,
  id,
  organization_id,
}) => {
  return {
    beacon_mac_address: beaconIdentifier,
    location: locationName,
    location_semantic: '',
    facility,
    is_active: true,
    organization_id,
    id,
  };
};
